import { useMutation, useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import React, { useEffect, useState, useRef, useId } from 'react';
import toast from 'react-hot-toast';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage.tsx';
import { createTrackingProps } from 'shared-utils/analytics';
import { ExtendedInboundTaskOption, Patient, client } from '../../api.tsx';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow from 'design-system/src/components/ButtonRow/ButtonRow';
import { FormLabel } from '../FormLabel.tsx';
import Typeahead, { TypeaheadOption } from 'design-system/src/components/Typeahead/Typeahead';
import { Dropdown, DropdownItem, DropdownContainer } from '../Dropdown.tsx';
import { AxiosError } from 'axios';
import { NewCommentFile, FormFilesDocumentTypes } from '../../api.tsx';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner.tsx';
import CommentAndFileSection from './CommentAndFileSection.tsx';
import { DropdownTypeaheadWithSections } from '../DropdownTypeaheadWithSections.tsx';
import { Typography } from 'design-system/src/components/Typography/Typography';
import styled from 'styled-components';
import {
  createRxOption,
  createPatientOption,
  RxData,
  FormContainer,
  StyledExtraInfoTable,
  StyledHairline,
  LoadingForm,
  TaskFormData,
} from './NewTaskModal.tsx';
import { inboundTaskOptionsQueryKey } from '../../queries.ts';
import { SelectChangeEvent } from '@mui/material';
import { FlagContents } from '../../api.tsx';

const AutoResponseInfoBox = styled.div`
  background-color: var(--grayscale-50);
  border: 1px solid var(--grayscale-50);
  border-radius: var(--border-radius-medium);
  padding: 24px 16px;
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: fadeIn 0.2s ease-out;
  opacity: 0;
  animation-fill-mode: forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

// This component is used for an InfoFlag or BlockingFlag, which are used to display relevant information
// to the user specific to the prescription, patient, and dropdown option
// If it's a BlockingFlag, the user can't submit a task for this option
const AutoResponseInfo = ({ flag }: { flag: FlagContents }) => {
  if (!flag) return null;

  return (
    <AutoResponseInfoBox>
      <Typography weight={600}>{flag.title}</Typography>
      <Typography>{flag.description}</Typography>
      {flag.link_text && flag.link_url && (
        <Button variant="link" href={flag.link_url} rel="noopener noreferrer">
          {flag.link_text}
        </Button>
      )}
    </AutoResponseInfoBox>
  );
};

export type ExtendedFormData = TaskFormData & {
  task_title: string;
  selectedTaskOption: ExtendedInboundTaskOption | null;
};

export const ExtendedNewTaskForm: React.FC<{
  onSubmit: (taskId: string) => void;
}> = (props) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [searchParams] = useSearchParams();
  const params = useParams();
  const patientLabelId = useId();
  const rxLabelId = useId();
  const taskTitleLabelId = useId();
  const taskCommentLabelId = useId();

  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const submit = useMutation<{ task_id: string }, AxiosError<{ error: string }>, ExtendedFormData>({
    mutationFn: async (data) => {
      // TODO: share this logic with NewFollowupTaskModal.tsx or migrate that modal to use this component
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking task creation, no real request sent.');

        // Simulating a successful response without sending the request
        return Promise.resolve({ task_id: 'mock-task-id' });
      }

      const formData = new FormData();
      formData.append('patient_id', data.patientId);
      formData.append('prescription_id', data.prescriptionId);
      formData.append('text', inputValue);
      const source = searchParams.get('source');
      if (source) {
        formData.append('task_source', source);
      }

      if (!data.selectedTaskOption) {
        throw new Error('Task title is required');
      }
      // TODO: pass up the task option value and convert to label in the back-end
      formData.append('task_title', data.selectedTaskOption.label);
      formData.append('type', data.selectedTaskOption.task_type);

      if (newFilesUploaded.length) {
        const formFilesDocumentTypes: FormFilesDocumentTypes = {};

        newFilesUploaded.forEach((fileInfo) => {
          formData.append('files[]', fileInfo.file);

          // We can't add the document type to the file directly,
          // so we track the types in a separate object
          // and use the file names as keys.
          formFilesDocumentTypes[fileInfo.file.name] = fileInfo.document_type;
        });

        formData.append('form_files_document_types', JSON.stringify(formFilesDocumentTypes));
      }

      return client.post(`tasks`, formData).then((res) => res.data);
    },
    onSuccess: ({ task_id }) => {
      props.onSubmit(task_id);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      Sentry.captureException(error);
      console.error(error);
    },
  });
  const { handleSubmit, control, formState, setValue, watch } = useForm<ExtendedFormData>({
    defaultValues: {
      patientId: searchParams.get('patientId') || '',
      prescriptionId: params.rxId || '',
      selectedTaskOption: null,
    },
  });
  const [isFetchingRxList, setIsFetchingRxList] = useState<boolean>(false);
  const [isFetchingPatients, setIsFetchingPatients] = useState<boolean>(true);
  const [rxList, setRxList] = useState<{ value: string; description: string }[]>([]);
  const [patients, setPatients] = useState([]);
  const [newFilesUploaded, setNewFilesUploaded] = useState<NewCommentFile[]>([]);
  const [preselectedPatient, setPreselectedPatient] = useState<TypeaheadOption | null>(null);
  const [preselectedRx, setPreselectedRx] = useState<TypeaheadOption | null>(null);
  const [selectedRxId, setSelectedRxId] = useState<string | null>(params.rxId || null);
  const navigate = useNavigate();

  const patientsQuery = useQuery({
    queryKey: ['patients'],
    queryFn: () => {
      return client.get(`patients`).then((res) => res.data);
    },
  });

  const getRxList = useMutation<{ rxs: RxData[] }, AxiosError<{ error: string }>, { patientId: string }>({
    mutationFn: async (data) => {
      return client.get(`rx_list/${data.patientId}`).then((res) => res.data);
    },
    onSuccess: async (res_data) => {
      setRxList(res_data.rxs.map((rxData: RxData) => createRxOption(rxData)));
      setIsFetchingRxList(false);
      const preselectedRx = res_data.rxs.find((rxData: RxData) => rxData.id === params.rxId);
      if (preselectedRx) {
        setPreselectedRx(createRxOption(preselectedRx));
      }
    },
    onError: async () => {
      toast.custom(() => <StandardErrorToast />);
      setIsFetchingRxList(false);
    },
  });

  const taskOptionsQuery = useQuery({
    queryKey: inboundTaskOptionsQueryKey(selectedRxId || ''),
    queryFn: () => {
      return client.get(`inbound-task-options/${selectedRxId}`).then((res) => res.data);
    },
    enabled: !!selectedRxId,
  });

  useEffect(() => {
    if (!patientsQuery.data || !patientsQuery.data.patients) {
      return;
    }
    setPatients(patientsQuery.data.patients.map((patient: Patient) => createPatientOption(patient)));
    setIsFetchingPatients(false);
    const preselectedPatient = patientsQuery.data.patients.find(
      (patient: Patient) => patient.id === searchParams.get('patientId'),
    );
    if (preselectedPatient) {
      setIsFetchingRxList(true);
      setPreselectedPatient(createPatientOption(preselectedPatient));
      getRxList.mutate({ patientId: preselectedPatient.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientsQuery.data, searchParams]);

  const handlePatientChange = (onChange: (patientId: string) => void, patientId: string) => {
    setSelectedRxId(null);
    setValue('selectedTaskOption', null);
    setValue('task_title', '');
    if (patientId) {
      onChange(patientId);
      setIsFetchingRxList(true);
      getRxList.mutate({ patientId: patientId });
    } else {
      setRxList([]);
    }
  };

  const handleRxChange = (onChange: (rxId: string) => void, event: SelectChangeEvent<unknown>) => {
    const rxId = event.target.value as string;
    onChange(rxId);
    setSelectedRxId(rxId);
    setValue('selectedTaskOption', null);
    setValue('task_title', '');
  };

  const isLoadingPreselectedRx = params.rxId && isFetchingRxList;
  const isLoadingPreselectedPatient = searchParams.get('patientId') && isFetchingPatients;
  const isLoadingTaskOptions = taskOptionsQuery.isLoading;

  if (isLoadingPreselectedRx || isLoadingPreselectedPatient || patientsQuery.isLoading) {
    return (
      <LoadingForm>
        <LoadingSpinner height="400px" />
      </LoadingForm>
    );
  }

  const extraInfoData = [];
  if (preselectedPatient) {
    extraInfoData.push({ label: 'Patient', value: preselectedPatient.description });
  }
  if (preselectedRx) {
    extraInfoData.push({ label: 'Prescription', value: preselectedRx.description });
  }

  const taskOptions = taskOptionsQuery.data?.task_options || [];
  const formattedTaskOptions = taskOptions.reduce(
    (acc: Record<string, { label: string; value: string }[]>, option: ExtendedInboundTaskOption) => {
      acc[option.option_bucket].push({
        label: option.label,
        value: option.value,
      });
      return acc;
    },
    Object.fromEntries(
      [...new Set(taskOptions.map((opt: ExtendedInboundTaskOption) => opt.option_bucket))].map((bucket) => [
        bucket,
        [],
      ]),
    ),
  );

  const handleTaskOptionChange = (onChange: (taskOption: string) => void, taskOptionVal: string) => {
    onChange(taskOptionVal);
    if (!taskOptionVal) {
      setValue('selectedTaskOption', null);
      return;
    }
    const selectedOption = taskOptions.find((option: ExtendedInboundTaskOption) => option.value === taskOptionVal);
    if (!selectedOption) {
      Sentry.captureException(`Task option not found: ${taskOptionVal}`);
    }
    setValue('selectedTaskOption', selectedOption);
  };

  return (
    <FormContainer onSubmit={handleSubmit((data) => submit.mutate(data))}>
      {!!extraInfoData.length && <StyledExtraInfoTable data={extraInfoData} hairlineComponent={<StyledHairline />} />}
      {!preselectedPatient && (
        <Controller
          name="patientId"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <DropdownContainer>
              <FormLabel id={patientLabelId}>Patient</FormLabel>
              <Typeahead
                labelId={patientLabelId}
                options={patients}
                onChange={(patientId: string) => {
                  handlePatientChange(field.onChange, patientId);
                }}
              />
            </DropdownContainer>
          )}
        />
      )}
      {!preselectedRx && (
        <Controller
          name="prescriptionId"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <DropdownContainer>
              <FormLabel id={rxLabelId}>Prescription</FormLabel>
              <Dropdown
                labelId={rxLabelId}
                value={field.value}
                onChange={(event) => handleRxChange(field.onChange, event)}
                disabled={!rxList.length || isFetchingRxList}
              >
                {rxList.map((rx) => (
                  <DropdownItem key={rx.value} value={rx.value}>
                    {rx.description}
                  </DropdownItem>
                ))}
              </Dropdown>
            </DropdownContainer>
          )}
        />
      )}
      <Controller
        name="task_title"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <DropdownContainer>
            <FormLabel id={taskTitleLabelId}>Task subject</FormLabel>
            <DropdownTypeaheadWithSections
              labelId={taskTitleLabelId}
              placeholder="Select a task subject"
              value={field.value}
              onChange={(value) => handleTaskOptionChange(field.onChange, value || '')}
              disabled={isLoadingTaskOptions || !selectedRxId}
              options={formattedTaskOptions}
            />
          </DropdownContainer>
        )}
      />
      {watch('selectedTaskOption')?.blocking_flag ? (
        <>
          <AutoResponseInfo flag={watch('selectedTaskOption')!.blocking_flag!} />
          <ButtonRow>
            <Button variant="tertiary" type="button" onClick={() => navigate(-1)} disabled={submit.isPending}>
              Cancel
            </Button>
          </ButtonRow>
        </>
      ) : (
        <div>
          {watch('selectedTaskOption')?.info_flag && (
            <AutoResponseInfo flag={watch('selectedTaskOption')!.info_flag!} />
          )}
          <FormLabel id={taskCommentLabelId}>Description of issue</FormLabel>
          <CommentAndFileSection
            files={newFilesUploaded}
            inputRef={inputRef}
            inputValue={inputValue}
            setInputValue={setInputValue}
            onFilesChange={setNewFilesUploaded}
            hideSubmitButton
            labelId={taskCommentLabelId}
            placeholder=""
          />
          <ButtonRow>
            <Button
              {...createTrackingProps({ elementName: 'Extended New Task Form: Cancel Button' })}
              variant="tertiary"
              type="button"
              onClick={() => navigate(-1)}
              disabled={submit.isPending}
            >
              Cancel
            </Button>
            <Button
              {...createTrackingProps({ elementName: 'Extended New Task Form: Submit Button' })}
              isLoading={submit.isPending}
              disabled={!formState.isValid || (!inputValue && !newFilesUploaded.length)}
              type="submit"
            >
              Submit
            </Button>
          </ButtonRow>
          <ErrorMessage visible={submit.isError}>{submit.error?.response?.data.error}</ErrorMessage>
        </div>
      )}
    </FormContainer>
  );
};

export default ExtendedNewTaskForm;
